import { parseLatLng, type LatLng } from './GeoMath';

/**
 * Turning "44 Elm Street, Concord MA" into a place to point the map at.
 *
 * ## Why coordinates come first
 *
 * {@link findPlace} tries {@link parseLatLng} before it touches the network, and that ordering is
 * the feature rather than an optimisation. Pasting a Google Maps URL is how most people already
 * carry a location between tools, it is exact where an address lookup is approximate, and it works
 * offline, unrate-limited, with no third party involved. An address search is the fallback for when
 * someone has words instead of numbers.
 *
 * ## Nominatim, and its rules
 *
 * OpenStreetMap's Nominatim is the only geocoder that needs no key, permits browser use and returns
 * CORS headers. It is also run on donated hardware with an explicit usage policy: one request per
 * second, no bulk querying, and identify your application. A browser cannot set `User-Agent`, so
 * identification rides on `Referer` and the `email` parameter is left for a deployment to fill in.
 *
 * The rate limit is honoured here rather than hoped for — {@link findPlace} refuses to fire faster
 * than once a second, and the UI debounces on top of that. A search box that hammers a free service
 * is how the free service stops being free.
 */

const NOMINATIM_ENDPOINT = 'https://nominatim.openstreetmap.org/search';

/** Nominatim's stated floor. Enforced, not assumed. */
const MIN_REQUEST_INTERVAL_MS = 1100;

export interface PlaceResult {
  at: LatLng;
  /** The full display name the geocoder returned. */
  label: string;
  /** What kind of thing it matched — `house`, `road`, `city`. Useful for ranking by hand. */
  kind: string;
  /** True when this came from parsed coordinates rather than a lookup. */
  exact: boolean;
}

export class GeocodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeocodeError';
  }
}

let lastRequestAt = 0;

/**
 * Find a place by coordinates, a map URL, or an address.
 *
 * Returns an empty list rather than throwing when the address simply matched nothing — "no results"
 * is an answer, and only a failed request is an error.
 */
export async function findPlace(query: string, signal?: AbortSignal): Promise<PlaceResult[]> {
  const text = query.trim();
  if (text.length === 0) return [];

  const coordinates = parseLatLng(text);
  if (coordinates) {
    return [{ at: coordinates, label: text, kind: 'coordinates', exact: true }];
  }

  const wait = Math.max(0, MIN_REQUEST_INTERVAL_MS - (Date.now() - lastRequestAt));
  if (wait > 0) await delay(wait, signal);
  lastRequestAt = Date.now();

  const url =
    `${NOMINATIM_ENDPOINT}?format=jsonv2&limit=5&addressdetails=0&q=${encodeURIComponent(text)}`;

  let response: Response;
  try {
    response = await fetch(url, signal ? { signal } : {});
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new GeocodeError(
      'The address lookup could not be reached. Paste coordinates or a Google Maps link instead — ' +
        'both work with no network service at all.',
    );
  }
  if (!response.ok) {
    throw new GeocodeError(`The address lookup answered HTTP ${response.status}.`);
  }

  const raw = (await response.json()) as unknown;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => entry as { lat?: string; lon?: string; display_name?: string; type?: string })
    .filter((entry) => entry.lat !== undefined && entry.lon !== undefined)
    .map((entry) => ({
      at: { lat: Number(entry.lat), lng: Number(entry.lon) },
      label: entry.display_name ?? 'Unnamed place',
      kind: entry.type ?? 'place',
      exact: false,
    }))
    .filter((result) => Number.isFinite(result.at.lat) && Number.isFinite(result.at.lng));
}

const NOMINATIM_REVERSE_ENDPOINT = 'https://nominatim.openstreetmap.org/reverse';

/**
 * The postal code covering a coordinate, or undefined when the lookup has none.
 *
 * Used by **Localize Site** when someone offers their browser's position: the app's climate table is
 * keyed by postal district, so a latitude and longitude have to become a ZIP before they mean
 * anything to it. Zoom 18 rather than the default, because a coarse reverse lookup returns a county
 * or a state and those carry no postcode at all.
 *
 * Shares {@link findPlace}'s rate limiter — the same free service, the same one-request-per-second
 * rule — and returns undefined rather than throwing on any failure. This sits behind a convenience
 * button; a geocoder having a bad day should leave the user typing five digits, not looking at an
 * error.
 */
export async function reverseGeocodePostalCode(
  at: LatLng,
  signal?: AbortSignal,
): Promise<string | undefined> {
  const wait = Math.max(0, MIN_REQUEST_INTERVAL_MS - (Date.now() - lastRequestAt));
  if (wait > 0) await delay(wait, signal);
  lastRequestAt = Date.now();

  const url =
    `${NOMINATIM_REVERSE_ENDPOINT}?format=jsonv2&zoom=18&addressdetails=1` +
    `&lat=${encodeURIComponent(String(at.lat))}&lon=${encodeURIComponent(String(at.lng))}`;

  // Its own deadline, because `fetch` has none: a request that never answers would leave the caller
  // awaiting it forever, and the caller is a button that disables itself while it waits.
  const deadline = new AbortController();
  const timer = setTimeout(() => deadline.abort(), REVERSE_TIMEOUT_MS);
  const onCallerAbort = (): void => deadline.abort();
  signal?.addEventListener('abort', onCallerAbort, { once: true });

  try {
    const response = await fetch(url, { signal: deadline.signal });
    if (!response.ok) return undefined;
    const raw = (await response.json()) as { address?: { postcode?: unknown } };
    const postcode = raw.address?.postcode;
    return typeof postcode === 'string' && postcode.length > 0 ? postcode : undefined;
  } catch (error) {
    // Only the *caller's* abort is worth propagating; our own deadline is an ordinary "no answer",
    // and this whole function is allowed to come back empty-handed.
    if (signal?.aborted === true && error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    return undefined;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onCallerAbort);
  }
}

/** How long to give the reverse lookup before treating it as unanswered. */
const REVERSE_TIMEOUT_MS = 8000;

/** Credit line required wherever Nominatim results are shown. */
export const GEOCODER_ATTRIBUTION = 'Address search © OpenStreetMap contributors (Nominatim)';

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}
